import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  Card,
  CardContent,
  Chip,
  Stack,
} from '@mui/material';
import {
  Home,
  People,
  Favorite,
  TrendingUp,
  Assessment,
  BarChart,
} from '@mui/icons-material';
import MetricsCard from '@/components/MetricsCard';
import AnalyticsChart from '@/components/AnalyticsChart';
import PropertyAnalytics from '@/components/PropertyAnalytics';
import ClientAnalytics from '@/components/ClientAnalytics';
import { AnalyticsService } from '@/services/analyticsService';
import { MatchingService } from '@/services/matchingService';
import { DashboardMetrics, MatchStatus } from '@/types';
import { useAuthStore } from '@/store/authStore';
import { useAppStore } from '@/store/appStore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AnalyticsPage: React.FC = () => {
  const { user } = useAuthStore();
  const { properties, clients } = useAppStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetrics | null>(null);
  const [matchMetrics, setMatchMetrics] = useState<any>(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const [metrics, matchPerf] = await Promise.all([
        AnalyticsService.getDashboardMetrics(),
        MatchingService.getMatchPerformanceMetrics(
          user?.role === 'agent' ? { agentId: user.id } : undefined
        ),
      ]);

      setDashboardMetrics(metrics);
      setMatchMetrics(matchPerf);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const statusDistributionData = matchMetrics?.statusDistribution.map((item: any) => ({
    name: item.status,
    value: item.count,
    percentage: item.percentage,
  })) || [];

  const conversionFunnelData = matchMetrics?.conversionFunnel
    ? [
        { name: 'Created', value: matchMetrics.conversionFunnel.created },
        { name: 'Viewed', value: matchMetrics.conversionFunnel.viewed },
        { name: 'Interested', value: matchMetrics.conversionFunnel.interested },
        { name: 'Scheduled', value: matchMetrics.conversionFunnel.scheduled },
        { name: 'Closed', value: matchMetrics.conversionFunnel.closed },
      ]
    : [];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Analytics Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your performance and gain insights into your real estate business
        </Typography>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Matches" />
        <Tab label="Properties" />
        <Tab label="Clients" />
      </Tabs>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricsCard
              title="Total Properties"
              value={dashboardMetrics?.totalProperties || 0}
              subtitle={`${dashboardMetrics?.activeProperties || 0} active`}
              icon={<Home />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricsCard
              title="Total Clients"
              value={dashboardMetrics?.totalClients || 0}
              subtitle={`${dashboardMetrics?.activeClients || 0} active`}
              icon={<People />}
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricsCard
              title="Total Matches"
              value={dashboardMetrics?.totalMatches || 0}
              subtitle={`${dashboardMetrics?.successfulMatches || 0} successful`}
              icon={<Favorite />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricsCard
              title="Conversion Rate"
              value={`${dashboardMetrics?.conversionRate.toFixed(1) || 0}%`}
              subtitle="Match success rate"
              icon={<TrendingUp />}
              color="warning"
            />
          </Grid>

          {/* Match Performance Chart */}
          <Grid item xs={12} md={6}>
            <AnalyticsChart
              title="Match Status Distribution"
              data={statusDistributionData}
              type="pie"
              dataKey="value"
              height={300}
            />
          </Grid>

          {/* Conversion Funnel */}
          <Grid item xs={12} md={6}>
            <AnalyticsChart
              title="Conversion Funnel"
              data={conversionFunnelData}
              type="bar"
              dataKey="value"
              xAxisKey="name"
              height={300}
            />
          </Grid>

          {/* Match Performance Summary */}
          {matchMetrics && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                    Match Performance Summary
                  </Typography>
                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Average Match Score
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {matchMetrics.averageScore}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Avg Conversion Rate
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {matchMetrics.averageConversionRate}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Avg Click-Through Rate
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {matchMetrics.averageClickThroughRate}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Matches
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {matchMetrics.totalMatches}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Matches Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {matchMetrics && (
            <>
              <Grid item xs={12} md={6}>
                <AnalyticsChart
                  title="Match Status Distribution"
                  data={statusDistributionData}
                  type="pie"
                  dataKey="value"
                  height={350}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AnalyticsChart
                  title="Conversion Funnel"
                  data={conversionFunnelData}
                  type="bar"
                  dataKey="value"
                  xAxisKey="name"
                  height={350}
                />
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                      Status Breakdown
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {matchMetrics.statusDistribution.map((item: any) => (
                        <Chip
                          key={item.status}
                          label={`${item.status}: ${item.count} (${item.percentage.toFixed(1)}%)`}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
        </Grid>
      </TabPanel>

      {/* Properties Tab */}
      <TabPanel value={tabValue} index={2}>
        <PropertyAnalytics properties={properties} />
      </TabPanel>

      {/* Clients Tab */}
      <TabPanel value={tabValue} index={3}>
        <ClientAnalytics clients={clients} />
      </TabPanel>
    </Box>
  );
};

export default AnalyticsPage;
